import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function ThemeToggle({ variant = 'default', className = '' }) {
  const { theme, toggleTheme } = useTheme();

  if (variant === 'icon') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={`relative h-9 w-9 rounded-lg transition-all duration-300 hover:bg-muted dark:hover:bg-slate-700 ${className}`}
              data-testid="theme-toggle-btn"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 text-amber-500" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 text-blue-400" />
              <span className="sr-only">Toggle theme</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Switch to {theme === 'dark' ? 'light' : 'dark'} mode</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={toggleTheme}
      className={`gap-2 ${className}`}
      data-testid="theme-toggle-btn"
    >
      {theme === 'dark' ? (
        <>
          <Sun className="h-4 w-4 text-amber-500" />
          <span>Light Mode</span>
        </>
      ) : (
        <>
          <Moon className="h-4 w-4 text-blue-500" />
          <span>Dark Mode</span>
        </>
      )}
    </Button>
  );
}

export function ThemeToggleDropdown() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex items-center gap-1 p-1 bg-muted dark:bg-slate-800 rounded-lg">
      <button
        onClick={() => setTheme('light')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${
          theme === 'light'
            ? 'bg-card dark:bg-slate-700 shadow-sm text-foreground dark:text-white'
            : 'text-muted-foreground hover:text-foreground dark:hover:text-slate-300'
        }`}
        data-testid="theme-light-btn"
      >
        <Sun className="h-4 w-4" />
        Light
      </button>
      <button
        onClick={() => setTheme('dark')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${
          theme === 'dark'
            ? 'bg-card dark:bg-slate-700 shadow-sm text-foreground dark:text-white'
            : 'text-muted-foreground hover:text-foreground dark:hover:text-slate-300'
        }`}
        data-testid="theme-dark-btn"
      >
        <Moon className="h-4 w-4" />
        Dark
      </button>
    </div>
  );
}
